import { observer } from 'mobx-react-lite';
import { useRef, useState, type ReactElement } from 'react';

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { logError } from '@/lib/errorLogging';
import { settingsStore } from '@/stores/settings.store';

const SYSTEM_DEFAULT_VALUE = '__system_default__';

type MicrophoneInputDeviceMenuProps = {
  children: ReactElement;
};

/** Right-click picker for the Chromium microphone used by browser speech capture */
export const MicrophoneInputDeviceMenu = observer(
  function MicrophoneInputDeviceMenu({
    children,
  }: MicrophoneInputDeviceMenuProps) {
    const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const loadRequestRef = useRef(0);
    const selectedDeviceId = settingsStore.preferredSpeechInputDeviceId;

    const loadDevices = async () => {
      const request = ++loadRequestRef.current;
      setDevices(null);
      setLoadFailed(false);
      try {
        const availableDevices =
          await navigator.mediaDevices.enumerateDevices();
        if (request !== loadRequestRef.current) return;
        setDevices(
          availableDevices.filter(
            (device) =>
              device.kind === 'audioinput' && device.deviceId !== 'default'
          )
        );
      } catch (error) {
        if (request !== loadRequestRef.current) return;
        logError(error, 'Failed to list microphone input devices');
        setLoadFailed(true);
      }
    };

    return (
      <ContextMenu
        onOpenChange={(open) => {
          if (open) void loadDevices();
        }}
      >
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent
          className="w-64 rounded-xl p-1 shadow-sm"
          aria-label="Microphone input device"
        >
          {loadFailed ? (
            <ContextMenuItem disabled>
              Could not load microphones
            </ContextMenuItem>
          ) : devices === null ? (
            <ContextMenuItem disabled>Loading microphones…</ContextMenuItem>
          ) : (
            <ContextMenuRadioGroup
              value={selectedDeviceId ?? SYSTEM_DEFAULT_VALUE}
              onValueChange={(value) =>
                settingsStore.setPreferredSpeechInputDeviceId(
                  value === SYSTEM_DEFAULT_VALUE ? null : value
                )
              }
            >
              <ContextMenuRadioItem value={SYSTEM_DEFAULT_VALUE}>
                System default
              </ContextMenuRadioItem>
              {selectedDeviceId &&
                !devices.some(
                  (device) => device.deviceId === selectedDeviceId
                ) && (
                  <ContextMenuRadioItem value={selectedDeviceId} disabled>
                    Selected microphone unavailable
                  </ContextMenuRadioItem>
                )}
              {devices.map((device, index) => (
                <ContextMenuRadioItem
                  key={device.deviceId}
                  value={device.deviceId}
                >
                  <span className="truncate">
                    {device.label || `Microphone ${index + 1}`}
                  </span>
                </ContextMenuRadioItem>
              ))}
            </ContextMenuRadioGroup>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  }
);
